/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Tessera Backend API
 * Tessera backend application with Bearer token authentication and multi-tenant support
 */
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  InvalidateOptions,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

import type { BodyType, ErrorType } from '../api-instance';
import { apiInstance } from '../api-instance';
import type {
  DeleteCategoryPathParameters,
  TLCategoryCreateRequest,
  TLCategoryResponse,
  TLCategoryUpdateRequest,
  TLHTTPValidationError,
  TLSuccessResponse,
  UpdateCategoryPathParameters,
} from './tessera-backend-api.schemas';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

const withQueryKey = <T extends object, K>(query: T, queryKey: K): T & { queryKey: K } => {
  const result = { queryKey } as T & { queryKey: K };
  for (const key of Object.keys(query)) {
    // The explicit queryKey always wins, matching the previous
    // `{ ...query, queryKey }` spread where it was set last.
    if (key === 'queryKey') continue;
    Object.defineProperty(result, key, {
      enumerable: true,
      configurable: true,
      get: () => (query as Record<string, unknown>)[key],
    });
  }
  return result;
};

/**
 * @summary Get All Categories
 */
export const getAllCategories = (options?: SecondParameter<typeof apiInstance>, signal?: AbortSignal) => {
  return apiInstance<TLCategoryResponse[]>(
    { url: `/v2/data-harmonization/categories`, method: 'GET', signal },
    options,
  );
};

export const getGetAllCategoriesQueryKey = () => {
  return ['v2', 'data-harmonization', 'categories'] as const;
};

export const getGetAllCategoriesQueryOptions = <
  TData = Awaited<ReturnType<typeof getAllCategories>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCategories>>, TError, TData>>;
  request?: SecondParameter<typeof apiInstance>;
}) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAllCategoriesQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAllCategories>>> = ({ signal }) =>
    getAllCategories(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getAllCategories>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetAllCategoriesQueryResult = NonNullable<Awaited<ReturnType<typeof getAllCategories>>>;
export type GetAllCategoriesQueryError = ErrorType<unknown>;

export function useGetAllCategories<TData = Awaited<ReturnType<typeof getAllCategories>>, TError = ErrorType<unknown>>(
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCategories>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllCategories>>,
          TError,
          Awaited<ReturnType<typeof getAllCategories>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllCategories<TData = Awaited<ReturnType<typeof getAllCategories>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCategories>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getAllCategories>>,
          TError,
          Awaited<ReturnType<typeof getAllCategories>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetAllCategories<TData = Awaited<ReturnType<typeof getAllCategories>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCategories>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Get All Categories
 */

export function useGetAllCategories<TData = Awaited<ReturnType<typeof getAllCategories>>, TError = ErrorType<unknown>>(
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCategories>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetAllCategoriesQueryOptions(options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Get All Categories
 */
export const prefetchGetAllCategoriesQuery = async <
  TData = Awaited<ReturnType<typeof getAllCategories>>,
  TError = ErrorType<unknown>,
>(
  queryClient: QueryClient,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getAllCategories>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getGetAllCategoriesQueryOptions(options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Get All Categories
 */
export const invalidateGetAllCategories = async (
  queryClient: QueryClient,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getGetAllCategoriesQueryKey() }, options);

  return queryClient;
};

/**
 * @summary Create Category
 */
export const createCategory = (
  tLCategoryCreateRequest: BodyType<TLCategoryCreateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCategoryResponse>(
    {
      url: `/v2/data-harmonization/categories`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: tLCategoryCreateRequest,
      signal,
    },
    options,
  );
};

export const getCreateCategoryQueryKey = (tLCategoryCreateRequest?: BodyType<TLCategoryCreateRequest>) => {
  return ['POST', 'v2', 'data-harmonization', 'categories', tLCategoryCreateRequest] as const;
};

export const getCreateCategoryQueryOptions = <
  TData = Awaited<ReturnType<typeof createCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCategoryCreateRequest: BodyType<TLCategoryCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCategory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getCreateCategoryQueryKey(tLCategoryCreateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof createCategory>>> = ({ signal }) =>
    createCategory(tLCategoryCreateRequest, requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof createCategory>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type CreateCategoryQueryResult = NonNullable<Awaited<ReturnType<typeof createCategory>>>;
export type CreateCategoryQueryError = ErrorType<TLHTTPValidationError>;

export function useCreateCategory<
  TData = Awaited<ReturnType<typeof createCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCategoryCreateRequest: BodyType<TLCategoryCreateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCategory>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCategory>>,
          TError,
          Awaited<ReturnType<typeof createCategory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCategory<
  TData = Awaited<ReturnType<typeof createCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCategoryCreateRequest: BodyType<TLCategoryCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCategory>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof createCategory>>,
          TError,
          Awaited<ReturnType<typeof createCategory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useCreateCategory<
  TData = Awaited<ReturnType<typeof createCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCategoryCreateRequest: BodyType<TLCategoryCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCategory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Create Category
 */

export function useCreateCategory<
  TData = Awaited<ReturnType<typeof createCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  tLCategoryCreateRequest: BodyType<TLCategoryCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCategory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getCreateCategoryQueryOptions(tLCategoryCreateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Create Category
 */
export const prefetchCreateCategoryQuery = async <
  TData = Awaited<ReturnType<typeof createCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  tLCategoryCreateRequest: BodyType<TLCategoryCreateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof createCategory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getCreateCategoryQueryOptions(tLCategoryCreateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Create Category
 */
export const invalidateCreateCategory = async (
  queryClient: QueryClient,
  tLCategoryCreateRequest: BodyType<TLCategoryCreateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getCreateCategoryQueryKey(tLCategoryCreateRequest) }, options);

  return queryClient;
};

/**
 * @summary Delete Category
 */
export const deleteCategory = (
  { categoryId }: DeleteCategoryPathParameters,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLSuccessResponse>(
    { url: `/v2/data-harmonization/categories/${categoryId}`, method: 'DELETE', signal },
    options,
  );
};

export const getDeleteCategoryQueryKey = ({ categoryId }: DeleteCategoryPathParameters) => {
  return ['DELETE', 'v2', 'data-harmonization', 'categories', categoryId] as const;
};

export const getDeleteCategoryQueryOptions = <
  TData = Awaited<ReturnType<typeof deleteCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { categoryId }: DeleteCategoryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCategory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getDeleteCategoryQueryKey({ categoryId });

  const queryFn: QueryFunction<Awaited<ReturnType<typeof deleteCategory>>> = ({ signal }) =>
    deleteCategory({ categoryId }, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: categoryId !== null && categoryId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof deleteCategory>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type DeleteCategoryQueryResult = NonNullable<Awaited<ReturnType<typeof deleteCategory>>>;
export type DeleteCategoryQueryError = ErrorType<TLHTTPValidationError>;

export function useDeleteCategory<
  TData = Awaited<ReturnType<typeof deleteCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCategoryPathParameters,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCategory>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCategory>>,
          TError,
          Awaited<ReturnType<typeof deleteCategory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCategory<
  TData = Awaited<ReturnType<typeof deleteCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCategoryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCategory>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof deleteCategory>>,
          TError,
          Awaited<ReturnType<typeof deleteCategory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useDeleteCategory<
  TData = Awaited<ReturnType<typeof deleteCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: DeleteCategoryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCategory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Delete Category
 */

export function useDeleteCategory<
  TData = Awaited<ReturnType<typeof deleteCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { categoryId }: DeleteCategoryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCategory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getDeleteCategoryQueryOptions({ categoryId }, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Delete Category
 */
export const prefetchDeleteCategoryQuery = async <
  TData = Awaited<ReturnType<typeof deleteCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { categoryId }: DeleteCategoryPathParameters,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof deleteCategory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getDeleteCategoryQueryOptions({ categoryId }, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Delete Category
 */
export const invalidateDeleteCategory = async (
  queryClient: QueryClient,
  { categoryId }: DeleteCategoryPathParameters,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries({ queryKey: getDeleteCategoryQueryKey({ categoryId }) }, options);

  return queryClient;
};

/**
 * @summary Update Category
 */
export const updateCategory = (
  { categoryId }: UpdateCategoryPathParameters,
  tLCategoryUpdateRequest: BodyType<TLCategoryUpdateRequest>,
  options?: SecondParameter<typeof apiInstance>,
  signal?: AbortSignal,
) => {
  return apiInstance<TLCategoryResponse>(
    {
      url: `/v2/data-harmonization/categories/${categoryId}`,
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      data: tLCategoryUpdateRequest,
      signal,
    },
    options,
  );
};

export const getUpdateCategoryQueryKey = (
  { categoryId }: UpdateCategoryPathParameters,
  tLCategoryUpdateRequest?: BodyType<TLCategoryUpdateRequest>,
) => {
  return ['PATCH', 'v2', 'data-harmonization', 'categories', categoryId, tLCategoryUpdateRequest] as const;
};

export const getUpdateCategoryQueryOptions = <
  TData = Awaited<ReturnType<typeof updateCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { categoryId }: UpdateCategoryPathParameters,
  tLCategoryUpdateRequest: BodyType<TLCategoryUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCategory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getUpdateCategoryQueryKey({ categoryId }, tLCategoryUpdateRequest);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof updateCategory>>> = ({ signal }) =>
    updateCategory({ categoryId }, tLCategoryUpdateRequest, requestOptions, signal);

  return {
    queryKey,
    queryFn,
    enabled: categoryId !== null && categoryId !== undefined,
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof updateCategory>>, TError, TData> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };
};

export type UpdateCategoryQueryResult = NonNullable<Awaited<ReturnType<typeof updateCategory>>>;
export type UpdateCategoryQueryError = ErrorType<TLHTTPValidationError>;

export function useUpdateCategory<
  TData = Awaited<ReturnType<typeof updateCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCategoryPathParameters,
  tLCategoryUpdateRequest: BodyType<TLCategoryUpdateRequest>,
  options: {
    query: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCategory>>, TError, TData>> &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateCategory>>,
          TError,
          Awaited<ReturnType<typeof updateCategory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateCategory<
  TData = Awaited<ReturnType<typeof updateCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCategoryPathParameters,
  tLCategoryUpdateRequest: BodyType<TLCategoryUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCategory>>, TError, TData>> &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof updateCategory>>,
          TError,
          Awaited<ReturnType<typeof updateCategory>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useUpdateCategory<
  TData = Awaited<ReturnType<typeof updateCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  pathParams: UpdateCategoryPathParameters,
  tLCategoryUpdateRequest: BodyType<TLCategoryUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCategory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
/**
 * @summary Update Category
 */

export function useUpdateCategory<
  TData = Awaited<ReturnType<typeof updateCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  { categoryId }: UpdateCategoryPathParameters,
  tLCategoryUpdateRequest: BodyType<TLCategoryUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCategory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getUpdateCategoryQueryOptions({ categoryId }, tLCategoryUpdateRequest, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>;
  };

  return withQueryKey(query, queryOptions.queryKey);
}

/**
 * @summary Update Category
 */
export const prefetchUpdateCategoryQuery = async <
  TData = Awaited<ReturnType<typeof updateCategory>>,
  TError = ErrorType<TLHTTPValidationError>,
>(
  queryClient: QueryClient,
  { categoryId }: UpdateCategoryPathParameters,
  tLCategoryUpdateRequest: BodyType<TLCategoryUpdateRequest>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof updateCategory>>, TError, TData>>;
    request?: SecondParameter<typeof apiInstance>;
  },
): Promise<QueryClient> => {
  const queryOptions = getUpdateCategoryQueryOptions({ categoryId }, tLCategoryUpdateRequest, options);

  await queryClient.prefetchQuery(queryOptions);

  return queryClient;
};

/**
 * @summary Update Category
 */
export const invalidateUpdateCategory = async (
  queryClient: QueryClient,
  { categoryId }: UpdateCategoryPathParameters,
  tLCategoryUpdateRequest: BodyType<TLCategoryUpdateRequest>,
  options?: InvalidateOptions,
): Promise<QueryClient> => {
  await queryClient.invalidateQueries(
    { queryKey: getUpdateCategoryQueryKey({ categoryId }, tLCategoryUpdateRequest) },
    options,
  );

  return queryClient;
};
